import React from 'react'

import { Formik } from 'formik'
import queryString from 'query-string'
import { useLocation } from 'react-router-dom'

import { ZariContext } from '_zari/context'

import { Form, Col, Row, Button, Divider, Alert } from '_zari/core'

import { PeriodoContext } from 'contexts/PeriodoContext'

import { TiposSocioConstant } from 'constants/index'

import { IdGen, RounderUtil, ZrSwal } from 'utils'

import * as AsientosContablesService from 'services/asientosContables.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'actualizar':
      return state.map(val => {
        if (val._idFila === action.payload._idFila) {
          val = {
            ...val,
            ...action.payload
          }
        }
        return val
      })
    case 'fill':
      return action.payload.map(x => ({
        ...x,
        _idFila: IdGen.newId()
      }))
    case 'limpiar':
      return []
    default:
      return state
  }
}

const MainForm = ({ resetMainForm }) => {
  const __zariContext = React.useContext(ZariContext)
  const __periodoContext = React.useContext(PeriodoContext)

  const location = useLocation()

  const [variasMonedas, setVariasMonedas] = React.useState(false)

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, [])

  const mainFormikRef = React.useRef(null)
  const formikPagoRef = React.useRef(null)
  const siguienteCorrelativoRef = React.useRef(null)

  React.useEffect(() => {
    if (formikPagoRef.current) {
      if (detalles.length > 0) {
        const monedas = [...new Set(detalles.filter(x => x.montoPago).map(x => x.moneda))]

        if (monedas.length === 0) {
          formikPagoRef.current.setFieldValue('montoTotal', '')
          formikPagoRef.current.setFieldValue('moneda', 'SOLES')
        } else if (monedas.length > 1) {
          setVariasMonedas(true)

          formikPagoRef.current.setFieldValue('montoTotal', '')
          formikPagoRef.current.setFieldValue('moneda', 'SOLES')
        } else {
          setVariasMonedas(false)

          const suma = detalles.reduce((accum, current) => RounderUtil.round(accum + parseFloat(current.montoPago || '0')), 0)

          if (suma > 0) {
            formikPagoRef.current.setFieldValue('montoTotal', suma)
            formikPagoRef.current.setFieldValue('moneda', monedas[0])
          } else {
            formikPagoRef.current.setFieldValue('montoTotal', '')
            formikPagoRef.current.setFieldValue('moneda', monedas[0])
          }
        }
      } else {
        formikPagoRef.current.setFieldValue('montoTotal', '')
        formikPagoRef.current.setFieldValue('moneda', 'SOLES')
      }
    }
  }, [detalles])

  React.useEffect(() => {
    let { tipoSocio } = queryString.parse(location.search)
    if (tipoSocio === TiposSocioConstant.CLIENTE) {
      AsientosContablesService.obtenerSiguienteCorrelativoOrigenCanje()
        .then(({ data }) => {
          siguienteCorrelativoRef.current = data.siguienteNumero
        })
        .catch(() => {
          __zariContext.snack('warning', 'No se pudo obtener el siguiente correlativo.')
        })
    }
  }, [__zariContext, location.search])

  const formInitialValues = React.useMemo(() => {
    let { tipoSocio } = queryString.parse(location.search)

    return {
      _idSocio: '',
      codigoSocio: '',
      documentoSocio: '',
      nombreSocio: '',
      tipoDocumentoSocio: '',
      tipoSocio: tipoSocio
    }
  }, [location.search])

  const clickBuscarArchivos = React.useCallback(
    ({ _idSocio, tipoSocio }) => {
      __zariContext.loader.open('Buscando documentos pendientes')
      AsientosContablesService.getPendientesxSocioxTipoSocio(_idSocio, tipoSocio)
        .then(({ data }) => {
          __zariContext.loader.close()

          if (data && data.length > 0) {
            detallesDispatcher({ type: 'fill', payload: data })
          } else {
            __zariContext.snack('warning', 'El cliente indicado no cuenta con documentos pendientes.')
          }
        })
        .catch(() => {
          __zariContext.snack('error', 'No se pudo obtener los documentos pendientes del cliente.')
          __zariContext.loader.close()
        })
    },
    [__zariContext]
  )

  const onSubmit = React.useCallback(
    (letrasData, { setSubmitting }) => {
      const formData = {
        ...mainFormikRef.current.values,
        ...letrasData,
        detalles: detalles
          .filter(x => x.montoPago)
          .map(x => ({
            _id: x._id,
            montoPago: parseFloat(x.montoPago)
          }))
      }

      AsientosContablesService.nuevoAsientoContableOgCanje(formData)
        .then(({ data }) => {
          ZrSwal.showAlert({
            tipo: 'success',
            mensaje: (
              <>
                <span>Operaciones Generadas :</span> <br />
                {data.operacionesGeneradas.map((x, k) => (
                  <React.Fragment key={k}>
                    <span>
                      {k + 1}. {x}
                    </span>{' '}
                    <br />
                  </React.Fragment>
                ))}
              </>
            ),
            onSuccess: () => {
              resetMainForm()
            }
          })
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
        .finally(() => {
          __periodoContext.actualizarEstados()
        })
    },
    [__zariContext, __periodoContext, detalles, resetMainForm]
  )

  return (
    <>
      <Row>
        <Col md={7}>
          <Formik innerRef={mainFormikRef} initialValues={formInitialValues} validateOnBlur={false} validateOnChange={false} validateOnMount={false}>
            {({ errors, isSubmitting, values, setValues }) => (
              <>
                <Form>
                  <Row>
                    <Col md={8}>
                      <FormGroups.SocioFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors._idSocio}
                        iTouched
                        iValue={values.nombreSocio}
                        setValues={setValues}
                        values={values}
                        vTipoSocio={values.tipoSocio}
                        dispatch={detallesDispatcher}
                      />
                    </Col>
                    <Col md={4}>
                      <Button onClick={() => clickBuscarArchivos(values)} block>
                        Ver Documentos
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
          </Formik>
          {detalles.length > 0 && (
            <>
              <Divider />
              <Sections.DocumentosPendientesSection filas={detalles} dispatch={detallesDispatcher} />
              {variasMonedas && <Alert color='danger'>No se puede pagar documentos con distinta moneda.</Alert>}
            </>
          )}
        </Col>
        {detalles.length > 0 && (
          <Col md={5}>
            <Sections.FormularioLetrasSection
              formikPagoRef={formikPagoRef}
              submitFunction={onSubmit}
              vTipoSocio={formInitialValues.tipoSocio}
              siguienteCorrelativoRef={siguienteCorrelativoRef}
            />
          </Col>
        )}
      </Row>
    </>
  )
}

export default React.memo(MainForm)
